import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2 } from "lucide-react";
import { Autocomplete } from "@/components/ui/autocomplete";
import { Button } from "@akuna/ui";
import { cn } from "@/lib/utils";
import type { Database } from "@/integrations/supabase/types";

type MedicationRow = Database["public"]["Tables"]["medications"]["Row"];
type LookupName = { id?: string; name?: string | null };
export type MedicationWithRelations = MedicationRow & {
  pharmaceutical_form?: string | LookupName | null;
  administration_route?: string | LookupName | null;
  brand?: LookupName | null;
  active_principles?: Array<{ active_principle: { name: string } }> | null;
  products?: Array<{ product: { id: string; name: string; brand?: { name: string | null } | null } }> | null;
  presentations?: unknown;
  source?: string | null;
};

export interface MedicationAutocompleteExtraOption {
  label: string;
  value: string;
  kind: "template";
  badgeLabel: string;
}

interface MedicationAutocompleteProps {
  medications: MedicationWithRelations[];
  isLoading?: boolean;
  value: string;
  selectedMedicationId?: string;
  onChange: (value: string) => void;
  onSelectMedication: (medication: MedicationRow, displayName: string) => void;
  onOpenCreateDialog?: (name: string) => void;
  onCreateNew?: (name: string) => void; // Callback para criar diretamente (sem abrir modal)
  extraOptions?: MedicationAutocompleteExtraOption[];
  onSelectExtraOption?: (option: MedicationAutocompleteExtraOption) => void;
  onFocus?: () => void;
  placeholder?: string;
  popoverPortalled?: boolean;
}

const ACTIVE_PRINCIPLE_TYPES = new Set(["principio_ativo", "active_principle", "ifa"]);

const toPascalCase = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/(^|[\s/-])([a-zà-ÿ])/g, (_match, separator: string, char: string) => `${separator}${char.toUpperCase()}`);

const getLookupName = (value: unknown): string => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && "name" in value) {
    const name = (value as { name?: unknown }).name;
    return typeof name === "string" ? name : "";
  }
  return "";
};

const formatName = (name?: string | null) => (name ? toPascalCase(name) : "");

const buildMedicationDetails = (medication: MedicationWithRelations, activePrincipleLabel: string) => {
  if (ACTIVE_PRINCIPLE_TYPES.has(medication.type ?? "")) {
    return [activePrincipleLabel];
  }

  const detailLines: string[] = [];
  const activePrinciples =
    medication.active_principles?.map((item) => formatName(item.active_principle.name)).filter(Boolean) ?? [];
  if (activePrinciples.length) {
    detailLines.push(`${activePrinciples.join(", ")}`);
  }

  const brandName = formatName(getLookupName(medication.brand));
  if (brandName) {
    detailLines.push(`${brandName}`);
  }



  if (detailLines.length === 0) {
    const customDetails = [medication.concentration]
      .filter(Boolean)
      .join(" · ");
    if (customDetails) {
      detailLines.push(customDetails);
    }
  }

  return detailLines;
};

export const MedicationAutocomplete = ({
  medications,
  isLoading = false,
  value,
  selectedMedicationId,
  onChange,
  onSelectMedication,
  onOpenCreateDialog,
  onCreateNew,
  extraOptions = [],
  onSelectExtraOption,
  onFocus,
  placeholder,
  popoverPortalled = true,
}: MedicationAutocompleteProps) => {
  const { t } = useTranslation("prescriptions");
  const resolvedPlaceholder = placeholder ?? t("medicationAutocomplete.placeholder");
  const sortedMedications = useMemo(
    () =>
      [...medications].sort((a, b) =>
        a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" }),
      ),
    [medications],
  );

  const options = useMemo(
    () => [
      ...extraOptions,
      ...sortedMedications.map((medication) => ({
        label: formatName(medication.name),
        value: medication.id,
      })),
    ],
    [extraOptions, sortedMedications],
  );

  const medicationsById = useMemo(
    () => new Map(sortedMedications.map((medication) => [medication.id, medication])),
    [sortedMedications],
  );

  const emptyMessage = isLoading ? (
    <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      {t("medicationAutocomplete.loading")}
    </div>
  ) : (
    <div className="px-3 py-4 text-sm text-muted-foreground">
      <p>{t("medicationAutocomplete.empty")}</p>
    </div>
  );

  // Verificar se o texto digitado não corresponde exatamente a nenhum medicamento
  const hasExactMatch = useMemo(() => {
    if (!value.trim()) return true; // Se não há texto, não mostrar botão
    const searchValue = value.toLowerCase().trim();
    return medications.some(med => 
      med.name.toLowerCase().trim() === searchValue
    );
  }, [medications, value]);

  const footer = value.trim() && !hasExactMatch ? (
    onCreateNew ? (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="w-full justify-start text-primary hover:bg-accent"
        onClick={(e) => {
          e.stopPropagation();
          onCreateNew(value);
        }}
      >
        {t("medicationAutocomplete.createActivePrinciple", { value })}
      </Button>
    ) : onOpenCreateDialog ? (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="w-full justify-start text-primary hover:bg-accent"
        onClick={(e) => {
          e.stopPropagation();
          onOpenCreateDialog(value);
        }}
      >
        {t("medicationAutocomplete.createMedication", { value })}
      </Button>
    ) : null
  ) : null;

  return (
    <Autocomplete
      options={options}
      value={value}
      onChange={onChange}
      onSelect={(option) => {
        const extraOption = extraOptions.find((entry) => entry.value === option.value);
        if (extraOption) {
          onSelectExtraOption?.(extraOption);
          return;
        }

        const medication = medicationsById.get(option.value);
        if (!medication) return;
        onSelectMedication(medication, option.label);
      }}
      selectedValue={selectedMedicationId ?? ""}
      onFocus={onFocus}
      placeholder={resolvedPlaceholder}
      emptyMessage={emptyMessage}
      footer={footer}
      popoverPortalled={popoverPortalled}
      preventParentScrollOnFocus
      useInlineDropdown
      renderOption={(option, isSelected) => {
        const extraOption = extraOptions.find((entry) => entry.value === option.value);
        if (extraOption) {
          return (
            <>
              <Check
                className={cn(
                  "mr-2 h-4 w-4",
                  isSelected ? "opacity-100" : "opacity-0",
                )}
              />
              <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
                <p className="min-w-0 truncate font-medium leading-none">{option.label}</p>
                <span className="shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                  {extraOption.badgeLabel}
                </span>
              </div>
            </>
          );
        }

        const medication = medicationsById.get(option.value);
        if (!medication) return option.label;

        const details = buildMedicationDetails(medication, t("medicationAutocomplete.activePrinciple"));
        return (
          <>
            <Check
              className={cn(
                "mr-2 h-4 w-4",
                isSelected ? "opacity-100" : "opacity-0",
              )}
            />
            <div className="flex-1">
              <p className="font-medium leading-none">{option.label}</p>
              {details.length > 0 && (
                <div className="mt-1 text-xs text-muted-foreground">
                  {details.map((detail, index) => (
                    <p key={`${option.value}-detail-${index}`}>{detail}</p>
                  ))}
                </div>
              )}
            </div>
          </>
        );
      }}
    />
  );
};
